import { Link } from "react-router-dom";
import { Bell, Search } from "lucide-react";
import { Button, cn } from "@aiw/ui";
import { LANGS, LANG_LABEL, LANG_SHORT } from "@aiw/i18n/react";
import { useMe } from "../hooks/useMe";
import { env } from "../lib/env";
import { useT } from "../lib/i18n";
import { FeedbackButton } from "../components/feedback-button";
import { TEXT_SIZES, useTextSize } from "../lib/text-size";

export function TopBar({ attentionCount = 0 }: { attentionCount?: number }) {
  const me = useMe();
  const { t, lang, setLang } = useT();
  const { size, setSize } = useTextSize();
  const initials = (me.data?.user.email ?? "??").slice(0, 2).toUpperCase();

  return (
    <header className="flex items-center gap-3 border-b bg-card px-4 py-2">
      <label className="relative flex-1 max-w-xl">
        <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <input className="h-9 w-full rounded-md border bg-background pl-8 pr-14 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring" placeholder={t("topbar.search")} aria-label={t("topbar.search")} />
        <kbd className="absolute right-2 top-2 rounded border bg-muted px-1.5 font-mono text-micro text-muted-foreground">⌘K</kbd>
      </label>
      <Link
        to="/attention"
        aria-label={t("topbar.immediateAttention", { count: attentionCount })}
        className={`flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm ${attentionCount > 0 ? "border-warning/50 bg-warning/10 text-warning" : "text-muted-foreground"}`}
      >
        <Bell className="h-4 w-4" />
        <span className="hidden xl:inline">{t("topbar.immediateAttentionLabel")}</span>
        <span className="num rounded-full bg-background px-1.5 text-micro font-semibold">{attentionCount}</span>
      </Link>
      <Button asChild size="sm"><Link to="/leads?new=1">{t("topbar.newLead")}</Link></Button>
      {/* Bug / Improve, on every page (Jacky, 12 October 2026). The dialog lives in the button. */}
      <FeedbackButton />
      <div className="flex items-center rounded-md border text-micro" role="group" aria-label={t("topbar.language")}>
        {/* Each language is named in its own script: whoever needs this switch is
            the person who cannot read the word "Chinese". */}
        {LANGS.map((l) => (
          <button
            key={l}
            type="button"
            lang={l === "zh" ? "zh-CN" : "en-AU"}
            aria-pressed={lang === l}
            title={`${t("topbar.languageSwitch")} · ${LANG_LABEL[l]}`}
            onClick={() => setLang(l)}
            className={cn("px-2.5 py-1.5 first:rounded-l-md last:rounded-r-md", lang === l ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
          >
            {LANG_SHORT[l]}
          </button>
        ))}
      </div>
      {/* Text size: named presets that fit, chosen per device (Jacky, 2026-10-04:
          "not pixel scale"). The monitor is the reason, so the choice stays in this
          browser like the language does. */}
      <label className="flex h-9 items-center gap-1.5 rounded-md border px-2 text-micro text-muted-foreground" title={t("topbar.textSize")}>
        <span className="font-display font-semibold text-foreground" aria-hidden>Aa</span>
        <select
          aria-label={t("topbar.textSize")}
          value={size}
          onChange={(e) => setSize(e.target.value as (typeof TEXT_SIZES)[number])}
          className="bg-transparent text-micro text-foreground focus:outline-none"
        >
          {TEXT_SIZES.map((s) => <option key={s} value={s}>{t(`textSize.${s}`)}</option>)}
        </select>
      </label>
      <span className="grid h-8 w-8 place-items-center rounded-full bg-rail font-mono text-micro text-rail-foreground" title={me.data?.user.email ?? ""}>{initials}</span>
      {env.mock && <span className="rounded bg-muted px-1.5 font-mono text-micro text-muted-foreground" title="No Supabase config: the CRM runs on the in-browser mock of services/api">{t("topbar.mock")}</span>}
    </header>
  );
}
